import { Component, computed, inject, input, LOCALE_ID, OnDestroy, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { finalize, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { TruckIcon } from '../../../../../shared/ui/icons/truck-icon/truck-icon';
import { PharmacistCartService } from '../../../../../services/cart/cart.pharmacist';
import { PharmacyCreditService } from '../../../../../services/pharmacy-credit/pharmacy-credit';
import { CartDto } from '../../../../../pages/admin-dashboard/cart-management/admin-cart.model';
import { addItemToCartFnReducer } from '../../../../../store/cart/cart.state';
import { Loading } from '../../../../../utils/loading/loading';

@Component({
  selector: 'app-product-details-card',
  standalone: true,
  imports: [CommonModule, TruckIcon, Loading],
  templateUrl: './product-details-card.html',
  styleUrl: './product-details-card.scss',
})
export class ProductDetailsCard implements OnDestroy {
  private pharmacistCartService = inject(PharmacistCartService);
  private pharmacyCreditService = inject(PharmacyCreditService);
  private store = inject(Store);
  private localeId = inject(LOCALE_ID);

  productId = input.required<string>();
  productTitle = input();
  mfgCompany = input();
  pharmacyPrice = input<string>();
  pharmacyDiscount = input();
  peoplePrice = input();
  unitPrice = input.required<number>();
  isOffer = input<boolean>(false);
  offerId = input<string>();
  // Product order limit for the selected pharmacy (null = no limit configured)
  remainingQuantity = input<number | null>(null);

  selectedPharmacy = computed(() => this.pharmacyCreditService.selectedPharmacy());
  isPharmacySuspended = computed(() => this.pharmacyCreditService.isSelectedPharmacySuspended());
  isArabic = computed(() => this.localeId.startsWith('ar'));
  isLimitReached = computed(() => {
    const remaining = this.remainingQuantity();

    return remaining != null && remaining <= 0;
  });

  quantity = signal(1);
  isAddingToCart = signal(false);
  addToCartError = signal('');
  addedToCart = signal(false);

  private addToCartSub?: Subscription;

  ngOnDestroy(): void {
    this.addToCartSub?.unsubscribe();
  }

  onIncreaseItem() {
    const remaining = this.remainingQuantity();

    if (remaining != null && this.quantity() >= remaining) return;

    this.quantity.set(this.quantity() + 1);
  }

  onDecreaseItem() {
    this.quantity.set(this.quantity() > 1 ? this.quantity() - 1 : this.quantity());
  }

  onAddToCart() {
    if (this.isAddingToCart() || this.isLimitReached()) return;

    this.addToCartError.set('');
    this.addedToCart.set(false);
    this.isAddingToCart.set(true);

    const dto: CartDto = !this.isOffer()
      ? {
          items: [
            {
              type: 'product',
              customerProductId: +this.productId(),
              quantity: this.quantity(),
            },
          ],
        }
      : {
          items: [
            {
              type: 'offer',
              offerId: +this.offerId()!,
              products: [
                {
                  customerProductId: +this.productId(),
                  quantity: this.quantity(),
                },
              ],
            },
          ],
        };

    this.addToCartSub?.unsubscribe();

    this.addToCartSub = this.pharmacistCartService
      .addItemToCart('product', this.selectedPharmacy()?.id!, dto)
      .pipe(finalize(() => this.isAddingToCart.set(false)))
      .subscribe({
        next: () => {
          this.addedToCart.set(true);

          this.store.dispatch(
            addItemToCartFnReducer({
              itemType: 'product',
              pharmacyId: this.selectedPharmacy()?.id!,
              dto,
            }),
          );

          setTimeout(() => this.addedToCart.set(false), 2000);
        },
        error: (err) => {
          this.addToCartError.set(
            this.isArabic() ? err?.messageAr || 'حدث بعض الخطأ' : err?.messageEn || 'Something went wrong',
          );
        },
      });
  }
}
